import { ReactNode, useMemo } from 'react';
import { Box, Typography } from '@mui/material';

import TableCard, { TableColumn } from '../common/TableCard';
import { AccountEvent } from '../../types/account';
import { formatEventDateTime } from '../../utils/dateTime';
import { renderText } from '../../utils/ui-text';
import { useThemeContext } from '../../contexts/BaseThemeContextProvider';

type AccountEventsTableProps<T extends AccountEvent> = {
  title?: string;
  events: T[];
  onRowClick?: (event: T) => void;
  renderActions?: (event: T) => ReactNode;
  getRowKey?: (event: T, index: number) => string | number;
  getAccountName?: (event: T) => ReactNode;
  hideColumns?: ('Title' | 'Type')[];
  showAssignedStaff?: boolean;
};

export default function AccountEventsTable<T extends AccountEvent>({
  title,
  events,
  onRowClick,
  renderActions,
  getRowKey,
  getAccountName,
  hideColumns = [],
  showAssignedStaff = false,
}: AccountEventsTableProps<T>): JSX.Element {
  const { theme } = useThemeContext();
  const columns = useMemo<Array<TableColumn<T>>>(() => {
    const baseColumns: Array<TableColumn<T>> = [];

    if (getAccountName) {
      baseColumns.push({
        label: 'Account',
        render: (event) => getAccountName(event) ?? '-',
      });
    }

    baseColumns.push({
      label: 'Event',
      render: (event) => {
        const title = event.title || 'Untitled Event';
        const dateStr = formatEventDateTime(event.event_start_date, event.event_end_date) ?? 'N/A';
        const locationStr = event.location_name || (event.location_id ? `Location #${event.location_id}` : '-');

        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', py: '4px' }}>
            <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary' }}>
              {title}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {dateStr}
            </Typography>
            {locationStr && locationStr !== '-' && (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {locationStr}
              </Typography>
            )}
          </Box>
        );
      },
    });

    baseColumns.push({
      label: 'Description',
      render: (event) =>
        renderText(theme.custom.pages.frost.authHome.typography.body1.color, event.description) || '-',
    });

    if (!hideColumns.includes('Type')) {
      baseColumns.push({
        label: 'Type',
        render: (event) => event.event_type || '-',
      });
    }

    if (showAssignedStaff) {
      baseColumns.push({
        label: 'Staff',
        render: (event) => {
          if (!event.assigned_staff || event.assigned_staff.length === 0) {
            return '-';
          }
          return event.assigned_staff
            .map((member) => {
              const firstName = member.first_name || (member.full_name ? member.full_name.split(' ')[0] : '');
              const lastName = member.last_name || (member.full_name ? member.full_name.split(' ').slice(1).join(' ') : '');
              const initial = lastName ? ` ${lastName.trim().charAt(0).toUpperCase()}` : '';
              return `${firstName.trim()}${initial}`.trim();
            })
            .filter(Boolean)
            .join(', ');
        },
      });
    }

    if (renderActions) {
      baseColumns.push({
        label: 'Actions',
        render: (event) => renderActions(event),
      });
    }

    return baseColumns;
  }, [getAccountName, renderActions, showAssignedStaff, hideColumns, theme]);

  const rowKeyGetter =
    getRowKey ??
    ((event: T, index: number) =>
      event.acc_event_id ?? `${event.acc_id ?? 'account'}-${event.event_start_date ?? index}`);

  return <TableCard title={title} rows={events} columns={columns} getRowKey={rowKeyGetter} onRowClick={onRowClick} />;
}
